import {
  naceCodes as fallbackNaceCodes,
  municipalities as fallbackMunicipalities,
  counties as fallbackCounties
} from '../data/brregCodeLists';

const BASE_URL = 'https://data.ssb.no/api/klass/v1';
const CACHE_MAX_AGE = 30 * 24 * 60 * 60 * 1000;

const CACHE_KEYS = {
  nace: 'saleswork_ssb_nace_codes',
  naceMeta: 'saleswork_ssb_nace_meta',
  municipalities: 'saleswork_ssb_municipalities',
  municipalitiesMeta: 'saleswork_ssb_municipalities_meta',
  counties: 'saleswork_ssb_counties',
  countiesMeta: 'saleswork_ssb_counties_meta'
};

const CLASSIFICATION_IDS = {
  nace: 6,
  municipalities: 131,
  counties: 104
};

const getStorageItem = key => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setStorageItem = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Local storage is optional. Runtime data remains usable without it.
  }
};

const readCache = (dataKey, metaKey) => {
  try {
    const data = JSON.parse(getStorageItem(dataKey) || 'null');
    const meta = JSON.parse(getStorageItem(metaKey) || 'null');

    if (!Array.isArray(data) || data.length === 0 || !meta?.importedAt) {
      return null;
    }

    const age = Date.now() - new Date(meta.importedAt).getTime();

    if (!Number.isFinite(age) || age < 0 || age > CACHE_MAX_AGE) {
      return null;
    }

    return {
      data,
      meta: {
        ...meta,
        source: 'SSB_KLASS',
        isComplete: true
      }
    };
  } catch {
    return null;
  }
};

const normalizeText = value => String(value || '').trim();

const getRawCode = item => (
  item?.code ??
  item?.kode ??
  item?.value ??
  item?.id ??
  ''
);

const getRawName = item => (
  item?.name ??
  item?.navn ??
  item?.label ??
  item?.shortName ??
  item?.beskrivelse ??
  ''
);

const getRawShortName = item => (
  item?.shortName ??
  item?.short_name ??
  item?.kortnavn ??
  null
);

const getRawParentCode = item => (
  item?.parentCode ??
  item?.parent_code ??
  item?.parent?.code ??
  item?.overordnetKode ??
  null
);

const getRawLevel = item => (
  item?.level ??
  item?.nivå ??
  item?.niva ??
  null
);

const normalizeNaceCode = input => {
  const value = normalizeText(input)
    .replace(/\s/g, '')
    .replace(',', '.');

  if (/^\d{5}$/.test(value)) {
    return `${value.slice(0, 2)}.${value.slice(2)}`;
  }

  if (/^\d{2}\.\d{3}$/.test(value)) {
    return value;
  }

  return value;
};

const normalizeMunicipalityCode = input => {
  const digits = normalizeText(input).replace(/\D/g, '');
  return digits ? digits.padStart(4, '0').slice(-4) : '';
};

const normalizeCountyCode = input => {
  const digits = normalizeText(input).replace(/\D/g, '');
  return digits ? digits.padStart(2, '0').slice(-2) : '';
};

const normalizeNaceItem = (item, importedAt) => {
  const code = normalizeNaceCode(getRawCode(item));
  const name = normalizeText(getRawName(item));
  const shortName = getRawShortName(item)
    ? normalizeText(getRawShortName(item))
    : null;
  const parentCode = getRawParentCode(item)
    ? normalizeNaceCode(getRawParentCode(item))
    : null;

  return {
    code,
    name,
    label: name,
    shortName,
    parentCode,
    level: getRawLevel(item),
    source: 'SSB_KLASS',
    standard: 'SN2025',
    classificationId: CLASSIFICATION_IDS.nace,
    importedAt,
    searchableText: [code, name, shortName]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
  };
};

const normalizeMunicipalityItem = (item, importedAt) => {
  const code = normalizeMunicipalityCode(getRawCode(item));
  const name = normalizeText(getRawName(item));
  const parentCode = getRawParentCode(item);
  const countyCode = parentCode
    ? normalizeCountyCode(parentCode)
    : normalizeCountyCode(code.slice(0, 2));

  return {
    code,
    name,
    countyCode: countyCode || null,
    countyName: normalizeText(item?.countyName || item?.fylke || ''),
    source: 'SSB_KLASS',
    classificationId: CLASSIFICATION_IDS.municipalities,
    importedAt,
    searchableText: [code, name, item?.countyName, item?.fylke]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
  };
};

const normalizeCountyItem = (item, importedAt) => {
  const code = normalizeCountyCode(getRawCode(item));
  const name = normalizeText(getRawName(item));

  return {
    code,
    name,
    source: 'SSB_KLASS',
    classificationId: CLASSIFICATION_IDS.counties,
    importedAt,
    searchableText: `${code} ${name}`.toLowerCase()
  };
};

const getCodesFromResponse = payload => {
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload?.codes,
    payload?._embedded?.codes,
    payload?.data?.codes,
    payload?.data?._embedded?.codes
  ];

  return candidates.find(Array.isArray) || [];
};

const getFallbackNace = () => {
  const importedAt = new Date().toISOString();

  return fallbackNaceCodes.map(item => ({
    ...normalizeNaceItem(item, importedAt),
    source: 'LOCAL_FALLBACK',
    standard: item.standard || 'SN2007',
    classificationId: CLASSIFICATION_IDS.nace
  }));
};

const getFallbackMunicipalities = () => {
  const importedAt = new Date().toISOString();

  return fallbackMunicipalities.map(item => ({
    ...normalizeMunicipalityItem(item, importedAt),
    source: 'LOCAL_FALLBACK',
    countyName: item.countyName || '',
    classificationId: CLASSIFICATION_IDS.municipalities
  }));
};

const getFallbackCounties = () => {
  const importedAt = new Date().toISOString();

  return fallbackCounties.map(item => ({
    ...normalizeCountyItem(item, importedAt),
    source: 'LOCAL_FALLBACK',
    classificationId: CLASSIFICATION_IDS.counties
  }));
};

const getFallbackResult = (data, warning) => ({
  data,
  meta: {
    source: 'LOCAL_FALLBACK',
    isComplete: false,
    importedAt: new Date().toISOString(),
    warning
  }
});

const getTodayDateString = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const fetchClassification = async classificationId => {
  const date = getTodayDateString();
  const codesAtUrl = `${BASE_URL}/classifications/${classificationId}/codesAt?date=${date}&language=nb`;
  const codesUrl = `${BASE_URL}/classifications/${classificationId}/codes?language=nb`;

  const request = async url => {
    const response = await fetch(url, {
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`SSB Klass svarte med HTTP ${response.status}.`);
    }

    const payload = await response.json();
    const codes = getCodesFromResponse(payload);

    if (codes.length === 0) {
      throw new Error('SSB Klass returnerte ingen gyldige koder.');
    }

    return codes;
  };

  try {
    return await request(codesAtUrl);
  } catch {
    return request(codesUrl);
  }
};

const fetchWithCacheFallback = async ({
  forceRefresh,
  dataKey,
  metaKey,
  classificationId,
  normalize,
  fallback,
  label
}) => {
  if (!forceRefresh) {
    const cached = readCache(dataKey, metaKey);

    if (cached) {
      return cached;
    }
  }

  try {
    const importedAt = new Date().toISOString();
    const rawCodes = await fetchClassification(classificationId);
    const data = rawCodes
      .map(item => normalize(item, importedAt))
      .filter(item => item.code && item.name);

    if (data.length === 0) {
      throw new Error(`SSB Klass returnerte ingen brukbare ${label}.`);
    }

    const meta = {
      source: 'SSB_KLASS',
      isComplete: true,
      classificationId,
      importedAt,
      count: data.length
    };

    setStorageItem(dataKey, data);
    setStorageItem(metaKey, meta);

    return { data, meta };
  } catch (error) {
    const cached = readCache(dataKey, metaKey);

    if (cached) {
      return cached;
    }

    return getFallbackResult(
      fallback(),
      `SSB Klass kunne ikke lastes. Lokal fallback brukes for ${label}.`
    );
  }
};

export const fetchSsbNaceCodes = ({ forceRefresh = false } = {}) => (
  fetchWithCacheFallback({
    forceRefresh,
    dataKey: CACHE_KEYS.nace,
    metaKey: CACHE_KEYS.naceMeta,
    classificationId: CLASSIFICATION_IDS.nace,
    normalize: normalizeNaceItem,
    fallback: getFallbackNace,
    label: 'NACE-koder'
  })
);

export const fetchSsbMunicipalities = ({ forceRefresh = false } = {}) => (
  fetchWithCacheFallback({
    forceRefresh,
    dataKey: CACHE_KEYS.municipalities,
    metaKey: CACHE_KEYS.municipalitiesMeta,
    classificationId: CLASSIFICATION_IDS.municipalities,
    normalize: normalizeMunicipalityItem,
    fallback: getFallbackMunicipalities,
    label: 'kommuner'
  })
);

export const fetchSsbCounties = ({ forceRefresh = false } = {}) => (
  fetchWithCacheFallback({
    forceRefresh,
    dataKey: CACHE_KEYS.counties,
    metaKey: CACHE_KEYS.countiesMeta,
    classificationId: CLASSIFICATION_IDS.counties,
    normalize: normalizeCountyItem,
    fallback: getFallbackCounties,
    label: 'fylker'
  })
);

export const getSsbCodeLists = async ({ forceRefresh = false } = {}) => {
  const [nace, municipalities, counties] = await Promise.all([
    fetchSsbNaceCodes({ forceRefresh }),
    fetchSsbMunicipalities({ forceRefresh }),
    fetchSsbCounties({ forceRefresh })
  ]);

  return {
    naceCodes: nace.data,
    municipalities: municipalities.data,
    counties: counties.data,
    metadata: {
      nace: nace.meta,
      municipalities: municipalities.meta,
      counties: counties.meta
    }
  };
};

const searchItems = (items, query, normalizeCode) => {
  const value = normalizeText(query).toLowerCase();

  if (!value) {
    return items;
  }

  const normalizedQuery = normalizeCode(value).toLowerCase();
  const compactQuery = value.replace(/[.\s,]/g, '');

  return items.filter(item => {
    const searchableText = String(item.searchableText || '').toLowerCase();
    const compactCode = String(item.code || '').replace('.', '');

    return searchableText.includes(value)
      || searchableText.includes(normalizedQuery)
      || String(item.code || '').toLowerCase().includes(normalizedQuery)
      || compactCode.includes(compactQuery);
  });
};

export const searchNaceCodes = async query => {
  const result = await fetchSsbNaceCodes();
  return searchItems(result.data, query, normalizeNaceCode);
};

export const searchMunicipalities = async query => {
  const result = await fetchSsbMunicipalities();
  return searchItems(result.data, query, normalizeMunicipalityCode);
};

export const searchCounties = async query => {
  const result = await fetchSsbCounties();
  return searchItems(result.data, query, normalizeCountyCode);
};

export {
  normalizeNaceCode,
  normalizeMunicipalityCode,
  normalizeCountyCode,
  CACHE_KEYS as ssbCacheKeys,
  CLASSIFICATION_IDS as ssbClassificationIds
};